import { z } from "zod";
import {
  actionSchema,
  addToCartServedSchema,
  navigateActionSchema,
  openCheckoutActionSchema,
  openSearchActionSchema,
  setCartQuantityServedSchema,
  showComparisonEnrichedSchema,
  showProductsEnrichedSchema,
  voiceActionSchema,
} from "@ai-guidance/shared";

/**
 * Acțiunile așa cum ajung ELE la SDK, pe sârmă.
 *
 * Diferă într-un punct de `commerceTextActionSchema`/`commerceVoiceActionSchema`
 * din shared: acolo `show_products` e forma pe care o EMITE modelul (doar
 * `variantId` + notă), iar backendul o îmbogățește din catalog înainte s-o
 * trimită mai departe. Dacă am valida răspunsul cu uniunea aceea, zod ar tăia
 * exact câmpurile îmbogățite (titlu, preț, imagine) și cardurile ar ajunge goale.
 * Deci: aceleași scheme partajate, recompuse cu `showProductsEnrichedSchema`.
 *
 * A doua diferență, din 10 aug 2026: acțiunile de coș vin în forma SERVITĂ,
 * fără `title`. Titlul pe care îl emite modelul e o dovadă că id-ul e al
 * produsului despre care vorbește - se verifică în backend și se taie acolo.
 * SDK-ul execută pe `variantId`, ca înainte, deci sârma rămâne neschimbată.
 *
 * Nimic nou inventat aici - doar altă compoziție a acelorași bucăți.
 */
const COMMERCE_WIRE_OPTIONS = [
  showProductsEnrichedSchema,
  // Comparația vine tot îmbogățită, din același motiv ca `show_products`: pe
  // sârmă ajung coloane complete (titlu, preț, imagine), nu id-urile emise de
  // model. `request_details` NU e aici și n-are ce căuta - e o unealtă de
  // server, consumată în backend, care nu ajunge niciodată la SDK.
  showComparisonEnrichedSchema,
  addToCartServedSchema,
  setCartQuantityServedSchema,
  openCheckoutActionSchema,
  openSearchActionSchema,
] as const;

/** Calea TEXT a unui app commerce - cu `navigate` din 27 aug 2026 (chatul navighează, nu mai randează carduri). */
export const commerceTextWireSchema = z.discriminatedUnion("type", [
  ...actionSchema.options,
  navigateActionSchema,
  ...COMMERCE_WIRE_OPTIONS,
]);

/** Tot ce poate executa SDK-ul: ghidare + navigare (voce) + coș. */
export const sdkActionSchema = z.discriminatedUnion("type", [
  ...voiceActionSchema.options,
  ...COMMERCE_WIRE_OPTIONS,
]);

export type CommerceTextWireAction = z.infer<typeof commerceTextWireSchema>;
/** Tipul acceptat de `ActionExecutor.run` - superset-ul tuturor căilor. */
export type SdkAction = z.infer<typeof sdkActionSchema>;
